import { DeliveryManagedBy, DeliveryMethod, InvoiceDetails } from "@/models/Customer";
import { InvoiceExportType } from "@/models/Organisation";

export const E_INVOICE_METHODS: DeliveryMethod[] = ["E_INVOICE_GLN", "E_INVOICE_ORG"];

export function isEInvoice(method?: DeliveryMethod) {
	return !!method && E_INVOICE_METHODS.includes(method);
}

/**
 * The stored delivery method, or — for data written before the migration —
 * the one org-service infers: GLN ⇒ E_INVOICE_GLN, else email ⇒ EMAIL, else PRINT.
 */
export function getEffectiveDeliveryMethod(
	invoiceDetails?: Pick<InvoiceDetails, "deliveryMethod" | "GLNCode" | "email">
): DeliveryMethod {
	if (invoiceDetails?.deliveryMethod) return invoiceDetails.deliveryMethod;
	if (invoiceDetails?.GLNCode?.trim()) return "E_INVOICE_GLN";
	if (invoiceDetails?.email?.trim()) return "EMAIL";
	return "PRINT";
}

/**
 * The methods offered in the picker.
 * - Visma (Bemannia): all four — Origo pushes the method.
 * - Fortnox: all four. An e-invoice pick is an intent until Fortnox has an
 *   active e-invoice koppling for the customer: the GLN is pushed to the
 *   kundkort and the invoice goes out as email/print until the koppling is
 *   activated in Fortnox, which then mirrors it back as managed (locked).
 * - Private persons: EMAIL and PRINT only.
 * - No invoice export configured: all four (nothing to restrict against).
 */
export function getDeliveryMethodOptions({
	privatePerson,
}: {
	exportType?: InvoiceExportType | string;
	privatePerson?: boolean;
	current?: DeliveryMethod;
}): DeliveryMethod[] {
	if (privatePerson) return ["EMAIL", "PRINT"];

	return ["E_INVOICE_GLN", "E_INVOICE_ORG", "EMAIL", "PRINT"];
}

/**
 * Whether the invoice field is editable for the method. Disabled fields keep
 * their value so it survives a method switch.
 * - Fortnox: email is always editable (it is the fallback and is synced to the
 *   kundkort); GLN is editable whenever Fortnox does not manage the customer,
 *   so it can be entered before the e-invoice koppling is active.
 * - Other export types: email only for EMAIL, GLN only for E_INVOICE_GLN.
 */
export function usesDeliveryField(
	method: DeliveryMethod,
	field: "email" | "GLNCode",
	{ exportType, managed }: { exportType?: InvoiceExportType | string; managed?: boolean } = {}
) {
	if (exportType === "FORTNOX") return field === "email" || !managed;

	return field === "email" ? method === "EMAIL" : method === "E_INVOICE_GLN";
}

/** A GLN code is exactly 13 digits. */
export const GLN_PATTERN = /^\d{13}$/;

/**
 * GLN validation shared by the customer and koncern forms (mirrors
 * org-service): a non-empty value must be 13 digits for every method, and
 * E_INVOICE_GLN requires one. Returns the i18n key of the problem, if any.
 */
export function getGLNError(
	method: DeliveryMethod | undefined,
	value: string | null | undefined
): string | undefined {
	const gln = value?.trim() ?? "";

	if (!gln) return method === "E_INVOICE_GLN" ? "deliveryMethod.field.GLNCode" : undefined;
	if (!GLN_PATTERN.test(gln)) return "deliveryMethod.field.GLNFormat";
	return undefined;
}

export function isManaged(invoiceDetails?: { deliveryManagedBy?: DeliveryManagedBy }) {
	return !!invoiceDetails?.deliveryManagedBy;
}

/** Field the method needs that the customer lacks, mirroring the backend validation. */
export function getMissingDeliveryField(
	method: DeliveryMethod,
	values: { GLNCode?: string; email?: string; vatNumber?: string }
): "GLNCode" | "vatNumber" | "email" | undefined {
	if (method === "E_INVOICE_GLN" && values.GLNCode?.trim().length !== 13) return "GLNCode";
	if (method === "E_INVOICE_ORG" && !values.vatNumber?.trim()) return "vatNumber";
	if (method === "EMAIL" && !values.email?.trim()) return "email";
	return undefined;
}

export type DeliveryErrorTarget = "GLNCode" | "email" | "deliveryMethod";

/**
 * Maps an org-service delivery error onto the invoice form. The `detail` is a
 * plain string naming the offending field:
 * - DELIVERY_METHOD_INCOMPLETE: "invoiceDetails.GLNCode", "vatNumber",
 *   "invoiceDetails.email" or "privatePerson" (e-invoice for a private person)
 * - DELIVERY_MANAGED_BY_FORTNOX: "invoiceDetails.deliveryMethod" or
 *   "invoiceDetails.GLNCode"
 * - INVALID_GLN_CODE: "invoiceDetails.GLNCode" (not 13 digits)
 * Returns the form field to mark and the i18n key of the readable message,
 * or undefined for any other error.
 */
export function getDeliveryErrorTarget(
	error: { code?: string; detail?: string } | undefined
): { field: DeliveryErrorTarget; message: string } | undefined {
	const detail = typeof error?.detail === "string" ? error.detail.trim() : "";

	if (error?.code === "DELIVERY_METHOD_INCOMPLETE") {
		switch (detail) {
			case "invoiceDetails.GLNCode":
				return { field: "GLNCode", message: "deliveryMethod.field.GLNCode" };
			case "invoiceDetails.email":
				return { field: "email", message: "deliveryMethod.field.email" };
			case "vatNumber":
				return { field: "deliveryMethod", message: "deliveryMethod.missingVatNumber" };
			case "privatePerson":
				return { field: "deliveryMethod", message: "deliveryMethod.privatePersonNoEInvoice" };
		}

		return { field: "deliveryMethod", message: "error.DELIVERY_METHOD_INCOMPLETE" };
	}

	if (error?.code === "DELIVERY_MANAGED_BY_FORTNOX") {
		return {
			field: detail === "invoiceDetails.GLNCode" ? "GLNCode" : "deliveryMethod",
			message: "error.DELIVERY_MANAGED_BY_FORTNOX",
		};
	}

	if (error?.code === "INVALID_GLN_CODE") {
		return { field: "GLNCode", message: "deliveryMethod.field.GLNFormat" };
	}

	return undefined;
}
